/**
 * Documents API Service
 * TanStack Query hooks for document management in client portal
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Query Keys
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters?: DocumentFilters) => [...documentKeys.lists(), filters] as const,
  detail: (id: string) => [...documentKeys.all, 'detail', id] as const,
  byMatter: (matterId: string) => [...documentKeys.all, 'matter', matterId] as const,
};

// Types
export type DocumentStatus = 'draft' | 'ready' | 'signature_required' | 'signed' | 'complete';

export interface DocumentSigner {
  id: string;
  name: string;
  email: string;
  signed: boolean;
  signedAt?: Date;
  signatureId?: string;
}

export interface Document {
  id: string;
  name: string;
  templateId?: string;
  templateName: string;
  matterId: string;
  matterName: string;
  matterNumber: string;
  clientId: string;
  status: DocumentStatus;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  downloadUrl?: string;
  signatureUrl?: string;
  signers?: DocumentSigner[];
  fileSize?: number;
  fileType?: string;
  version?: number;
}

export interface DocumentFilters {
  status?: DocumentStatus | DocumentStatus[];
  matterId?: string;
  clientId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreateDocumentInput {
  name: string;
  templateId: string;
  matterId: string;
  data?: Record<string, unknown>;
}

export interface RequestSignatureInput {
  documentId: string;
  signers: Array<{
    name: string;
    email: string;
  }>;
  message?: string;
  dueDate?: Date;
}

// Mock data for development
const mockDocuments: Document[] = [
  {
    id: 'doc-1',
    name: 'Warranty Deed - 123 Main St',
    templateId: 'tmpl-1',
    templateName: 'Wisconsin Warranty Deed',
    matterId: 'matter-1',
    matterName: 'Property Purchase - 123 Main St',
    matterNumber: '2024-0089-REP',
    clientId: 'client-1',
    status: 'signature_required',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
    updatedAt: new Date(Date.now() - 1000 * 60 * 30),
    createdBy: 'Sarah Johnson',
    signatureUrl: '/sign/doc-1',
    signers: [
      { id: 's1', name: 'John Smith', email: 'john@example.com', signed: false },
      { id: 's2', name: 'Jane Smith', email: 'jane@example.com', signed: false },
    ],
    fileType: 'application/pdf',
    fileSize: 245000,
    version: 1,
  },
  {
    id: 'doc-2',
    name: 'Operating Agreement - Smith Holdings LLC',
    templateId: 'tmpl-2',
    templateName: 'LLC Operating Agreement',
    matterId: 'matter-2',
    matterName: 'LLC Formation',
    matterNumber: '2024-0092-BUF',
    clientId: 'client-1',
    status: 'ready',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 12),
    createdBy: 'Sarah Johnson',
    downloadUrl: '/api/documents/doc-2/download',
    fileType: 'application/pdf',
    fileSize: 189000,
    version: 2,
  },
  {
    id: 'doc-3',
    name: 'Articles of Organization',
    templateId: 'tmpl-3',
    templateName: 'Wisconsin LLC Articles',
    matterId: 'matter-2',
    matterName: 'LLC Formation',
    matterNumber: '2024-0092-BUF',
    clientId: 'client-1',
    status: 'complete',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    createdBy: 'Sarah Johnson',
    downloadUrl: '/api/documents/doc-3/download',
    fileType: 'application/pdf',
    fileSize: 156000,
    version: 1,
  },
  {
    id: 'doc-4',
    name: 'Property Deed - 456 Oak Ave',
    templateId: 'tmpl-1',
    templateName: 'Wisconsin Warranty Deed',
    matterId: 'matter-3',
    matterName: 'Property Sale - 456 Oak Ave',
    matterNumber: '2024-0078-REP',
    clientId: 'client-1',
    status: 'signed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10),
    createdBy: 'Sarah Johnson',
    downloadUrl: '/api/documents/doc-4/download',
    signers: [
      {
        id: 's3',
        name: 'John Smith',
        email: 'john@example.com',
        signed: true,
        signedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10)
      },
    ],
    fileType: 'application/pdf',
    fileSize: 267000,
    version: 1,
  },
  {
    id: 'doc-5',
    name: 'Purchase Agreement Draft',
    templateId: 'tmpl-4',
    templateName: 'Real Estate Purchase Agreement',
    matterId: 'matter-1',
    matterName: 'Property Purchase - 123 Main St',
    matterNumber: '2024-0089-REP',
    clientId: 'client-1',
    status: 'draft',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
    createdBy: 'Maria Garcia',
    fileType: 'application/pdf',
    fileSize: 312000,
    version: 3,
  },
];

// API Functions
async function fetchDocuments(filters?: DocumentFilters): Promise<Document[]> {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 300));

  let filtered = [...mockDocuments];

  if (filters?.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    filtered = filtered.filter((d) => statuses.includes(d.status));
  }

  if (filters?.matterId) {
    filtered = filtered.filter((d) => d.matterId === filters.matterId);
  }

  if (filters?.clientId) {
    filtered = filtered.filter((d) => d.clientId === filters.clientId);
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(
      (d) =>
        d.name.toLowerCase().includes(search) ||
        d.matterName.toLowerCase().includes(search) ||
        d.matterNumber.toLowerCase().includes(search) ||
        d.templateName.toLowerCase().includes(search)
    );
  }

  // Sort by updatedAt descending
  filtered.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  if (filters?.limit) {
    const offset = filters.offset || 0;
    filtered = filtered.slice(offset, offset + filters.limit);
  }

  return filtered;
}

async function fetchDocument(id: string): Promise<Document> {
  await new Promise((resolve) => setTimeout(resolve, 200));

  const document = mockDocuments.find((d) => d.id === id);

  if (!document) {
    throw new Error('Document not found');
  }

  return document;
}

async function fetchDocumentsByMatter(matterId: string): Promise<Document[]> {
  return fetchDocuments({ matterId });
}

async function createDocument(input: CreateDocumentInput): Promise<Document> {
  await new Promise((resolve) => setTimeout(resolve, 500));

  const newDocument: Document = {
    id: `doc-${Date.now()}`,
    name: input.name,
    templateId: input.templateId,
    templateName: 'Generated Document',
    matterId: input.matterId,
    matterName: 'Current Matter',
    matterNumber: '2024-XXXX-NEW',
    clientId: 'client-1',
    status: 'draft',
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy: 'Current User',
    version: 1,
  };

  mockDocuments.unshift(newDocument);
  return newDocument;
}

async function requestSignature(input: RequestSignatureInput): Promise<Document> {
  await new Promise((resolve) => setTimeout(resolve, 400));

  const docIndex = mockDocuments.findIndex((d) => d.id === input.documentId);
  if (docIndex === -1) {
    throw new Error('Document not found');
  }

  const document = mockDocuments[docIndex];
  const updatedDocument: Document = {
    ...document,
    status: 'signature_required',
    updatedAt: new Date(),
    signatureUrl: `/sign/${document.id}`,
    signers: input.signers.map((s, idx) => ({
      id: `signer-${Date.now()}-${idx}`,
      name: s.name,
      email: s.email,
      signed: false,
    })),
  };

  mockDocuments[docIndex] = updatedDocument;
  return updatedDocument;
}

async function downloadDocument(id: string): Promise<Blob> {
  await new Promise((resolve) => setTimeout(resolve, 300));

  // In real implementation, this would fetch from storage
  // For now, create a placeholder PDF blob
  const content = `Document ID: ${id}\nThis is a placeholder document.`;
  return new Blob([content], { type: 'application/pdf' });
}

async function signDocument(documentId: string, signerId: string): Promise<Document> {
  await new Promise((resolve) => setTimeout(resolve, 500));

  const docIndex = mockDocuments.findIndex((d) => d.id === documentId);
  if (docIndex === -1) {
    throw new Error('Document not found');
  }

  const document = mockDocuments[docIndex];
  const updatedSigners = document.signers?.map((s) =>
    s.id === signerId ? { ...s, signed: true, signedAt: new Date() } : s
  );

  const allSigned = updatedSigners?.every((s) => s.signed);

  const updatedDocument: Document = {
    ...document,
    status: allSigned ? 'signed' : 'signature_required',
    updatedAt: new Date(),
    signers: updatedSigners,
  };

  mockDocuments[docIndex] = updatedDocument;
  return updatedDocument;
}

// Statistics
export interface DocumentStats {
  total: number;
  byStatus: Record<DocumentStatus, number>;
  needingAction: number;
  recentlyUpdated: number;
}

async function fetchDocumentStats(clientId?: string): Promise<DocumentStats> {
  const documents = await fetchDocuments({ clientId });

  const stats: DocumentStats = {
    total: documents.length,
    byStatus: {
      draft: 0,
      ready: 0,
      signature_required: 0,
      signed: 0,
      complete: 0,
    },
    needingAction: 0,
    recentlyUpdated: 0,
  };

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  documents.forEach((doc) => {
    stats.byStatus[doc.status]++;
    if (doc.status === 'signature_required' || doc.status === 'ready') {
      stats.needingAction++;
    }
    if (doc.updatedAt >= oneWeekAgo) {
      stats.recentlyUpdated++;
    }
  });

  return stats;
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch list of documents with optional filtering
 */
export function useDocuments(filters?: DocumentFilters) {
  return useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: () => fetchDocuments(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch a single document by ID
 */
export function useDocument(id: string) {
  return useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => fetchDocument(id),
    enabled: !!id,
  });
}

/**
 * Fetch documents for a specific matter
 */
export function useDocumentsByMatter(matterId: string) {
  return useQuery({
    queryKey: documentKeys.byMatter(matterId),
    queryFn: () => fetchDocumentsByMatter(matterId),
    enabled: !!matterId,
  });
}

/**
 * Fetch document statistics
 */
export function useDocumentStats(clientId?: string) {
  return useQuery({
    queryKey: [...documentKeys.all, 'stats', clientId] as const,
    queryFn: () => fetchDocumentStats(clientId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new document
 */
export function useCreateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDocument,
    onSuccess: (newDocument) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.setQueryData(documentKeys.detail(newDocument.id), newDocument);
    },
  });
}

/**
 * Request signatures for a document
 */
export function useRequestSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestSignature,
    onSuccess: (updatedDocument) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.setQueryData(documentKeys.detail(updatedDocument.id), updatedDocument);
    },
  });
}

/**
 * Sign a document
 */
export function useSignDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, signerId }: { documentId: string; signerId: string }) =>
      signDocument(documentId, signerId),
    onSuccess: (updatedDocument) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.setQueryData(documentKeys.detail(updatedDocument.id), updatedDocument);
    },
  });
}

/**
 * Download a document
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (id: string) => {
      const blob = await downloadDocument(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return id;
    },
  });
}

// =============================================================================
// PREFETCH
// =============================================================================

/**
 * Prefetch document for faster navigation
 */
export function usePrefetchDocument() {
  const queryClient = useQueryClient();

  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: documentKeys.detail(id),
      queryFn: () => fetchDocument(id),
    });
  };
}

export default {
  useDocuments,
  useDocument,
  useDocumentsByMatter,
  useDocumentStats,
  useCreateDocument,
  useRequestSignature,
  useSignDocument,
  useDownloadDocument,
  usePrefetchDocument,
  documentKeys,
};
